import { useQuery } from '@tanstack/react-query'

import type {
  DevelopmentTask,
  DevelopmentTaskPreviewReceipt,
  DevelopmentTaskReleaseReceipt,
} from '../../app/development-tasks'
import { developmentTaskPreviewReceiptKey } from '../../app/preview-receipt-key'
import { developmentTaskReleaseReceiptKey } from '../../app/release-receipt-key'
import { Button } from '../../components/button'
import { buttonClass } from '../../components/button-style'
import { Card, CardHint, CardTitle } from '../../components/card'
import { Tag } from '../../components/tag'
import { t } from '../../copy'
import {
  factsFromTask,
  missionFromFacts,
  requestStateCopy,
  requestStateTone,
  statusCopy,
  statusTone,
} from './delivery-mission-copy'

export type LoadPreviewReceipt = (
  projectId: string,
  taskId: string,
) => Promise<DevelopmentTaskPreviewReceipt>

export type LoadReleaseReceipt = (
  projectId: string,
  taskId: string,
) => Promise<DevelopmentTaskReleaseReceipt>

/**
 * 交付任务只读展示预览与发布事实。V2 由平台在 AI 交出精确候选后自动交付。
 *
 * preview_url 可空。有真实 https 地址才打开预览。
 * recorded/executing 不是已上线。
 */
export function DeliveryMissionFromPreviewLane({
  task,
  loadReceipt,
  loadRelease,
}: {
  task: DevelopmentTask
  loadReceipt?: LoadPreviewReceipt
  loadRelease?: LoadReleaseReceipt
}) {
  const receipt = useQuery({
    queryKey: developmentTaskPreviewReceiptKey(task.id),
    queryFn: () => {
      if (!loadReceipt) {
        throw new Error('development_task.preview.latest_unavailable')
      }
      return loadReceipt(task.project_id, task.id)
    },
    enabled: Boolean(loadReceipt && task.id && task.project_id),
    retry: (count, error) => {
      const status = error && typeof error === 'object' && 'status' in error ? error.status : undefined
      const kind = error && typeof error === 'object' && 'kind' in error ? error.kind : undefined
      if (status === 404 || kind === 'missing') return false
      return count < 1
    },
  })
  const release = useQuery({
    queryKey: developmentTaskReleaseReceiptKey(task.id),
    queryFn: () => {
      if (!loadRelease) {
        throw new Error('development_task.release.latest_unavailable')
      }
      return loadRelease(task.project_id, task.id)
    },
    enabled: Boolean(loadRelease && task.id && task.project_id),
    retry: (count, error) => {
      const status = error && typeof error === 'object' && 'status' in error ? error.status : undefined
      const kind = error && typeof error === 'object' && 'kind' in error ? error.kind : undefined
      if (status === 404 || kind === 'missing') return false
      return count < 1
    },
  })
  return (
    <DeliveryMissionPanel
      task={task}
      receipt={receipt.data}
      release={release.data}
    />
  )
}

export function DeliveryMissionPanel({
  task,
  receipt,
  release,
}: {
  task: DevelopmentTask
  receipt?: DevelopmentTaskPreviewReceipt
  release?: DevelopmentTaskReleaseReceipt
}) {
  const mission = missionFromFacts(factsFromTask(task, receipt, release))

  return (
    <Card data-testid="development-task-mission">
      <CardTitle>{t('development_task.mission.panel.title')}</CardTitle>
      <CardHint className="mt-npc-gap-sm leading-[1.6]">{t('development_task.mission.panel.hint')}</CardHint>
      {mission.issues.length > 0 ? (
        <ul className="mt-npc-card mb-0 list-disc pl-npc-gap-lg text-npc-caption" data-testid="development-task-mission-issues">
          {mission.issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : null}
      <div className="mt-npc-card flex flex-wrap gap-npc-gap-sm">
        {mission.actions.fix === 'available' ? (
          <Button data-testid="development-task-mission-fix">
            {t('development_task.mission.status.needs_fix')}
          </Button>
        ) : null}
        <Tag tone={statusTone[mission.status]} data-testid="development-task-mission-status">
          {t(statusCopy[mission.status])}
        </Tag>
        {mission.requestState ? (
          <Tag tone={requestStateTone[mission.requestState]} data-testid="development-task-mission-request-state">
            {t(requestStateCopy[mission.requestState])}
          </Tag>
        ) : null}
      </div>
      <dl className="mt-npc-card mb-0 grid grid-cols-[minmax(96px,0.5fr)_minmax(0,1fr)] gap-x-npc-gap-lg gap-y-npc-gap-sm text-npc-caption">
        <Row label={t('development_task.mission.field.owner_sha')} value={mission.ownerSha} testId="development-task-mission-owner-sha" />
        <Row label={t('development_task.mission.field.integration_sha')} value={mission.integrationSha} testId="development-task-mission-integration-sha" />
        <Row label={t('development_task.mission.field.production_sha')} value={mission.productionSha} testId="development-task-mission-production-sha" />
        <Row label={t('development_task.mission.field.preview_url')} value={mission.previewUrl} testId="development-task-mission-preview-url" />
        <dt className="text-npc-ink-softer">{t('development_task.mission.field.next_step')}</dt>
        <dd className="m-0 text-npc-ink-medium" data-testid="development-task-mission-next">{t(mission.nextStep)}</dd>
      </dl>
      <div className="mt-npc-card flex flex-wrap gap-npc-gap-sm">
        {mission.actions.openPreview === 'available' && mission.previewUrl ? (
          <a
            className={buttonClass({ variant: 'secondary' })}
            href={mission.previewUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="development-task-mission-open-preview"
          >
            {t('development_task.mission.action.open_preview')}
          </a>
        ) : (
          <Button variant="secondary" disabled data-testid="development-task-mission-open-preview">
            {t('development_task.mission.action.open_preview')}
          </Button>
        )}
      </div>
    </Card>
  )
}

function Row({ label, value, testId }: { label: string; value: string | null; testId: string }) {
  return (
    <>
      <dt className="text-npc-ink-softer">{label}</dt>
      <dd className="m-0 break-all font-npc-mono text-npc-ink-medium" data-testid={testId}>
        {value ?? t('development_task.mission.unavailable')}
      </dd>
    </>
  )
}
